import { FC, KeyboardEvent, MouseEvent, ReactNode, useCallback, useEffect, useId, useRef, useState } from "react";
import { AnimationResult, animated, useTransition } from "react-spring";

import { Shelf } from "capitalrx-components-2.0/lib/layout";
import { Subheading } from "capitalrx-components-2.0/lib/typography";

import { BaseProps } from "../../BaseProps";
import { createComponentStyleHook } from "../../tokens";
import { setDocumentOverflow } from "../../utilities";
import { getHtmlPropsFromBaseProps } from "../../utilities/getHtmlPropsFromBaseProps";
import { AlertProvider } from "../Alert";
import { Button, CloseButtonAppearance } from "../Button";
import { OverlayContainer } from "../Overlay";
import { TooltipContainer } from "../Tooltip";
import { DrawerCloseEventHandler } from "./DrawerCloseEventHandler";

import legacyStyles from "./legacy/styles.module.scss";
import tokenStyles from "./styles.module.css";

/**
 * The `Drawer` component properties.
 */
export interface DrawerProps extends BaseProps {
  /**
   * The position of the drawer. If not specified defaults to `"right"`.
   */
  readonly position?: "left" | "right" | "bottom";

  /**
   * The drawer title.
   */
  readonly title?: ReactNode;

  /**
   * The drawer content.
   */
  readonly children?: ReactNode;

  /**
   * The drawer footer content.
   */
  readonly footer?: ReactNode;

  /**
   * Indicates whether the drawer is visible.
   */
  readonly visible?: boolean;

  /**
   * The size of the drawer. If not specified defaults to `"narrow"`.
   */
  readonly size?: "narrow" | "wide" | "ultra-wide";

  /**
   * The optional root template for the drawer.
   */
  readonly root?: (children?: ReactNode) => ReactNode;

  /**
   * Called when the close button is clicked.
   */
  readonly onClose?: DrawerCloseEventHandler;
  /**
   * The id for the Drawer dialog element, used for accessibility.
   */
  readonly id?: string;
}

const useStyles = createComponentStyleHook<DrawerProps>({
  legacyStyles,
  tokenStyles,
  componentTokens: "drawer",
});

/**
 * A component which displays a Drawer.
 */
export const Drawer: FC<DrawerProps> = (props) => {
  const { position = "right", title, visible = false, children, footer, size = "narrow", root, onClose, testId, id } = props;
  const { mode, styles, StyleModeBoundary } = useStyles(props);
  const [enterState, setEnterState] = useState<"entering" | "exiting" | null>(null);
  const dialogRef = useRef<HTMLDialogElement | null>(null);
  const [showContent, setShowContent] = useState(false);

  let offscreenTransform = "translateX(100%)";
  if (position === "left") offscreenTransform = "translateX(-100%)";
  else if (position === "bottom") offscreenTransform = "translateY(100%)";
  const onscreenTransform = position === "bottom" ? "translateY(0%)" : "translateX(0%)";

  const uniqueId = useId();
  const dialogId = id ?? `drawer-dialog-${uniqueId}`;
  const titleId = `drawer-title-${uniqueId}`;
  const descriptionId = `drawer-description-${uniqueId}`;
  const previouslyFocusedElementRef = useRef<HTMLElement | null>(null);

  const transitions = useTransition(visible ? [visible] : [], {
    config: { duration: 300 },
    enter: { state: "entering", transform: onscreenTransform },
    from: { state: "exiting", transform: offscreenTransform },
    leave: { state: "exiting", transform: offscreenTransform },
    onStart: (result: AnimationResult) => {
      setEnterState((result.value as { state: "entering" | "exiting" }).state);
    },
  });

  const baseHtmlProps = getHtmlPropsFromBaseProps({
    className: [styles.Drawer, "drawer", size, enterState, `position-${position}`],
    testId: "drawer",
  });

  const refHandler = useCallback((ref: HTMLDialogElement | null) => {
    dialogRef.current = ref;
    if (!ref) {
      setShowContent(false);
      return;
    }

    ref.showModal();

    /*
      We have to do this because the <dialog> element automatically focuses the first focusable element unless another has 'autofocus=true' on it.
      however, React never adds this attribute if you set autoFocus={true} so the first element is always the close button (if available) which
      causes autoFocus elements to immediately lose focus. So what we do is as soon as the modal is added to the DOM and needs to be shown, we
      tick a frame and only then bring in the content, ensuring React forces focus away from the close button and to the relevant element.
    */
    requestAnimationFrame(() => {
      setShowContent(true);
    });
  }, []);

  const keyDownHandler = useCallback(
    (ev: KeyboardEvent<HTMLDialogElement>) => {
      if (ev.code === "Escape") {
        ev.preventDefault();
        ev.stopPropagation();
        onClose?.();
      }
    },
    [onClose],
  );

  const mouseDownHandler = useCallback(
    (ev: MouseEvent<HTMLDialogElement>) => {
      if (ev.button !== 0) return;
      if (!dialogRef.current) return;
      if (ev.target !== ev.currentTarget) return;

      onClose?.();
    },
    [onClose],
  );

  useEffect(() => {
    setDocumentOverflow(visible ? "hidden" : "");
    return () => setDocumentOverflow("");
  }, [visible]);

  useEffect(() => {
    if (visible && dialogRef.current) {
      const focusable = dialogRef.current.querySelector("[tabindex], button, [href], input, select, textarea");
      (focusable as HTMLElement)?.focus();
    }
  }, [visible]);

  useEffect(() => {
    if (visible) {
      previouslyFocusedElementRef.current = document.activeElement as HTMLElement;
    } else {
      if (previouslyFocusedElementRef.current) {
        previouslyFocusedElementRef.current.focus();
        previouslyFocusedElementRef.current = null;
      }
    }
  }, [visible]);

  /* Listen for the dialog's native "cancel" event (e.g. Escape),
     prevent the browser's default close, and delegate closing to onClose;
     cleanup the listener on unmount/changes. */
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    const handleCancel = (ev: Event) => {
      ev.preventDefault();
      onClose?.();
    };
    dialog.addEventListener("cancel", handleCancel);
    return () => dialog.removeEventListener("cancel", handleCancel);
  }, [onClose]);

  const content = (
    <div className={styles.ContentRoot} data-testid={testId}>
      <div className={styles.header} data-testid="drawer-header">
        <Shelf alignment="top" spacing="medium-large">
          <Subheading id={titleId}>{title}</Subheading>
          <Shelf.Spacer />
          {!!onClose && (
            <Button appearance={CloseButtonAppearance} icon="x-close" tooltip="Close" onClick={onClose} testId="drawer-close" />
          )}
        </Shelf>
      </div>
      {showContent && !!children && <div className={styles.content}>{children}</div>}
      {showContent && !!footer && <div className={styles.footer}>{footer}</div>}
    </div>
  );

  return (
    <StyleModeBoundary mode={mode}>
      {transitions((style) => (
        <animated.dialog
          {...baseHtmlProps}
          ref={refHandler}
          style={{ ...baseHtmlProps.style, ...style }}
          id={dialogId}
          onKeyDown={keyDownHandler}
          onMouseDown={mouseDownHandler}
          tabIndex={-1}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          aria-describedby={descriptionId}
        >
          <div className="client-area" id={descriptionId}>
            <AlertProvider>
              <OverlayContainer>
                <TooltipContainer>{root ? root(content) : content}</TooltipContainer>
              </OverlayContainer>
            </AlertProvider>
          </div>
        </animated.dialog>
      ))}
    </StyleModeBoundary>
  );
};
